<script setup>
import {computed, inject} from "vue";
import {Link, usePage} from "@inertiajs/vue3";
import {track} from "@/Composables/useAnalytics.js";


// Ziggy's route() via injection: the global `route` only exists in the browser, so calling it in
// script code breaks server-side rendering ("ReferenceError: route is not defined").
const route = inject('route');
const props = defineProps({
    // cue_card | topic_questions | sample_exam | blog_post | cue_cards_list | question_topics_list | sample_exams_list | exam_material
    pageType: {
        type: String,
        required: true
    },
    itemId: {
        type: [String, Number],
        default: null
    },
    // 'top' sits right under the question (compact), 'bottom' closes the page (full card).
    placement: {
        type: String,
        default: 'bottom'
    },
    headline: {
        type: String,
        default: null
    },
    body: {
        type: String,
        default: null
    }
});

const page = usePage();
const isAuthenticated = computed(() => !!page.props.is_authenticated);

// The AI test draws a random IELTS question set, so copy promises "a cue card like this", never "this cue card".
const copy = {
    cue_card: {
        headline: 'Now say it out loud: answer a cue card like this and get an instant AI band score',
        body: 'Reading a sample answer is not the same as speaking for two minutes under pressure. An AI examiner gives you a real Part 2 topic and scores you on all four IELTS criteria.'
    },
    topic_questions: {
        headline: 'Practise questions like these out loud with an AI examiner',
        body: 'Get a band score for fluency, vocabulary, grammar and pronunciation, plus the exact mistakes to fix.'
    },
    sample_exam: {
        headline: 'Take a speaking test like this one and get your band score',
        body: 'An AI examiner asks IELTS-style questions, then scores you on all four criteria and shows you what to fix.'
    },
    blog_post: {
        headline: 'Put this into practice: take a speaking test with an AI examiner',
        body: 'Get a band score on all four IELTS criteria and the exact mistakes to fix.'
    },
    default: {
        headline: 'Practise IELTS speaking out loud and get an instant AI band score',
        body: 'An AI examiner asks IELTS-style questions and scores you on fluency, vocabulary, grammar and pronunciation.'
    }
};

const content = computed(() => {
    const base = copy[props.pageType] || copy.default;
    return {
        headline: props.headline || base.headline,
        body: props.body || base.body
    };
});

const href = computed(() => isAuthenticated.value
    ? route('ai_peer.start_screen', {session_mode: 'quick', track: 'ielts'})
    // intent=practice sends the new user straight to a quick test after sign-up (RegisteredUserController::create).
    : route('register', {intent: 'practice', track: 'ielts'}));

const buttonLabel = computed(() => isAuthenticated.value ? 'Start a 5-minute test' : 'Practise now — free');

const onClick = () => track('content_cta_clicked', {
    page_type: props.pageType,
    item_id: props.itemId,
    placement: props.placement,
    is_authenticated: isAuthenticated.value
});
</script>

<template>
    <aside
        :class="[
            'not-prose rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-white dark:border-primary-800 dark:from-gray-800 dark:to-gray-900',
            placement === 'top' ? 'p-4 sm:p-5 my-4' : 'p-6 sm:p-8 my-8'
        ]">
        <div :class="['flex gap-4', placement === 'top' ? 'flex-col sm:flex-row sm:items-center sm:justify-between' : 'flex-col']">
            <div class="flex items-start gap-3">
                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white"
                      aria-hidden="true">
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round"
                              d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z"/>
                    </svg>
                </span>
                <div>
                    <p :class="['font-bold text-gray-900 dark:text-white', placement === 'top' ? 'text-base sm:text-lg' : 'text-lg sm:text-2xl']">
                        {{ content.headline }}
                    </p>
                    <p v-if="placement !== 'top'" class="mt-2 text-gray-600 dark:text-gray-300">
                        {{ content.body }}
                    </p>
                </div>
            </div>

            <div :class="['flex flex-col', placement === 'top' ? 'sm:items-end shrink-0' : 'sm:flex-row sm:items-center gap-3']">
                <Link :href="href"
                      @click.capture="onClick"
                      class="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:focus:ring-primary-800">
                    {{ buttonLabel }}
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
                         aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                    </svg>
                </Link>
                <p v-if="!isAuthenticated"
                   :class="['mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center', placement === 'top' ? '' : 'sm:mt-0']">
                    5 free tests · No card needed
                </p>
            </div>
        </div>
    </aside>
</template>
